// Automatisierung-Panel — Cowork-Scheduled-Task-Ergebnisse.
//
// Zeigt je Task den JUENGSTEN Lauf: Status-Ampel (grün ok / gelb
// partial|skip / rot abort), relative Zeit (aus run_iso) + Kurz-Summary.
// Self-fetching (eigene useQuery, Muster wie AnomalyStreamTile). Mobile-
// First (Tailwind sm-Default). Quelle: GET /api/v1/task-runs/recent.

import { useQuery } from "@tanstack/react-query";

import {
  CoworkTaskRunRecent,
  CoworkTaskRunsRecentResponse,
  TaskRunStatus,
  fetchTaskRunsRecent,
} from "../../lib/api";

// Ampel: grün ok / gelb partial|skip / rot abort.
const STATUS_STYLE: Record<TaskRunStatus, string> = {
  ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  partial: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  skip: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  abort: "bg-rose-500/15 text-rose-300 border-rose-500/40",
};

const STATUS_LABEL: Record<TaskRunStatus, string> = {
  ok: "OK",
  partial: "Teilweise",
  skip: "Übersprungen",
  abort: "Abbruch",
};

// Relative Zeit aus run_iso; age_minutes (Server) als Fallback.
function relativeTime(iso: string, ageMinutes: number | null): string {
  let mins = ageMinutes;
  if (mins == null) {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "—";
    mins = (Date.now() - t) / 60_000;
  }
  if (mins < 1) return "gerade eben";
  if (mins < 60) return `vor ${Math.round(mins)} min`;
  const hours = mins / 60;
  if (hours < 24) return `vor ${Math.round(hours)} h`;
  const days = Math.floor(hours / 24);
  return `vor ${days} ${days === 1 ? "Tag" : "Tagen"}`;
}

export default function TaskRunsPanel() {
  const query = useQuery<CoworkTaskRunsRecentResponse, Error>({
    queryKey: ["task-runs", "recent"],
    queryFn: () => fetchTaskRunsRecent(50),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: 1,
  });

  const tasks = query.data?.tasks ?? [];

  return (
    <section
      aria-label="Automatisierung"
      className="rounded-2xl border border-slate-700/50 bg-slate-900/40 p-4 sm:p-5 space-y-3"
    >
      <header className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-100">Automatisierung</h3>
        <span className="text-[10px] text-slate-400">
          {tasks.length > 0 ? `${tasks.length} Tasks` : "Cowork-Schedules"}
        </span>
      </header>

      {query.isLoading && <p className="text-xs text-slate-400">Lade Läufe …</p>}

      {!query.isLoading && !query.data?.available && (
        <p className="text-xs text-slate-400 py-1">
          {query.data?.setup_hint ??
            "Noch keine Task-Läufe. Cowork-Schedules melden hier ihren letzten Lauf."}
        </p>
      )}

      {tasks.length > 0 && (
        <ul className="space-y-1.5">
          {tasks.map((t) => (
            <TaskRow key={t.task_id} run={t} />
          ))}
        </ul>
      )}
    </section>
  );
}

function TaskRow({ run }: { run: CoworkTaskRunRecent }) {
  return (
    <li className="flex items-start gap-2 text-xs">
      <span
        className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_STYLE[run.status]}`}
      >
        {STATUS_LABEL[run.status]}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate font-medium text-slate-200">
            {run.task_id}
            {run.firma_id && (
              <span className="text-slate-500"> · {run.firma_id}</span>
            )}
          </span>
          <span className="shrink-0 tabular-nums text-slate-500">
            {relativeTime(run.run_iso, run.age_minutes)}
          </span>
        </div>
        {run.summary && (
          <p className="mt-0.5 text-slate-400 line-clamp-2">{run.summary}</p>
        )}
      </div>
    </li>
  );
}
